import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../contexts/PermissionsContext'

// Loading component
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  )
}

// Protected Route component
function ProtectedRoute({ children, requireAuth = true, redirectTo = '/login' }) {
    const { isAuthenticated, isLoading } = useAuth()
    const location = useLocation()

    // Show loading spinner while checking authentication
    if (isLoading) {
      return <LoadingSpinner />
    }

    // If route requires authentication and user is not authenticated
    if (requireAuth && !isAuthenticated) {
      // Save the attempted location for redirecting after login
      return <Navigate to={redirectTo} state={{ from: location }} replace />
    }

    // If user is authenticated, render the protected component
    return children
}



// Public Route component (for login, register, etc.)
export function PublicRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />
  }

  // If user is authenticated, redirect to menu
  if (isAuthenticated) {
    // Get the intended destination from location state, or default to /menu
    const from = location.state?.from?.pathname || '/menu'
    return <Navigate to={from} replace />
  }
  
  // Render the public component
  return children
}

// Organization Route component (requires organization selection)
export function OrganizationRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check if user has organizations or needs to create one
  // This could be enhanced to check organization context
  return children
}






// User Route component (restricts regular users to transfers only)
export function UserRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return children
}

// Role Protected Route component (requires specific roles)
export function RoleProtectedRoute({ 
  children, 
  requiredRoles = [], 
  requireAll = false, 
  redirectTo = '/menu',
  fallbackMessage = 'Acesso negado. Você não tem permissío para acessar esta página.' 
}) {
  const { isAuthenticated, isLoading } = useAuth()
  const { hasRole, hasAllRoles, hasAnyRole, loading: permissionsLoading } = usePermissions()
  const location = useLocation()

  // Show loading spinner while checking authentication or permissions
  if (isLoading || permissionsLoading) {
    return <LoadingSpinner />
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const hasAccess = requireAll 
      ? hasAllRoles(requiredRoles)
      : hasAnyRole(requiredRoles)

    if (!hasAccess) {
      // Show access denied message or redirect
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md">
            <div className="text-red-500 text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Acesso Negado</h2>
            <p className="text-gray-600 mb-4">{fallbackMessage}</p>
            <button 
              onClick={() => window.history.back()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
            >
              Voltar
            </button>
          </div>
        </div>
      )
    }
  }

  return children
}


export default ProtectedRoute